import cn from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { Textarea } from 'core-components/library/inputs'
import { PopPaper } from 'core-components/library/popup'

import { IFinSobValidationError } from '../../models/IFinSobValidationError'

import { FinSobValidationPopupHeader } from './components/finSobValidationPopupHeader/FinSobValidationPopupHeader'
import { getReplaceParams } from './helpers/getReplaceParams'
import tr from './translations'

import styles from './FinSobValidationPopupContent.module.scss'

interface IProps {
  validationErrors: IFinSobValidationError[]

  inputCount: number
  inputIndex: number
  inputsUnconfirmed: number
  onNextInput: () => void
  onPrevInput: () => void
  onSelectInput: (value: number) => void

  onApply: (value: string) => Promise<void>
  onCancel: () => void
}

/**
 * Confirmation of potentially incorrect changes in Fin and SOB values
 */
export const FinSobValidationPopupContent = ({
  validationErrors,

  inputCount,
  inputIndex,
  inputsUnconfirmed,
  onNextInput,
  onPrevInput,
  onSelectInput,

  onApply,
  onCancel,
}: IProps) => {
  /** -------------------- States ----------------------- */

  const [newJustificationMessage, setNewJustificationMessage] = useState(
    validationErrors.length > 0 ? (validationErrors[0].justificationMessage ?? '') : ''
  )

  /** -------------------- Hooks ----------------------- */

  const t = useSafetyTranslation()

  /** -------------------- Variables ----------------------- */

  const isShowJustificationMessage = useMemo(() => {
    return validationErrors.filter((el) => el.isJustificationRequired).length > 0
  }, [validationErrors])

  const isApplyEnabled = useMemo(() => {
    return (
      validationErrors.filter((el) => el.isJustificationRequired).length === 0 ||
      !!newJustificationMessage
    )
  }, [newJustificationMessage, validationErrors])

  const groupByCategory = useMemo(() => {
    return validationErrors.reduce<Record<string, IFinSobValidationError[]>>((acc, error) => {
      if (!acc[error.category]) {
        acc[error.category] = []
      }
      acc[error.category].push(error)

      return acc
    }, {})
  }, [validationErrors])

  const groupByLabel = useMemo(() => {
    return validationErrors.reduce<Record<string, IFinSobValidationError[]>>((acc, error) => {
      const key = error.label ?? ''
      if (key) {
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(error)
      }

      return acc
    }, {})
  }, [validationErrors])

  const groupedValidationErrors = useMemo(() => {
    if (Object.keys(groupByLabel).length) {
      return groupByLabel
    }

    return groupByCategory
  }, [groupByCategory, groupByLabel])

  const isShowNavigation = useMemo(() => {
    return inputCount > 1
  }, [inputCount])

  const applyButtonTranslation = useMemo(() => {
    return isShowNavigation && inputsUnconfirmed > 1 ? tr.confirm : tr.save
  }, [inputsUnconfirmed, isShowNavigation])

  const commonReplaceParams = useMemo(() => {
    let firstValidationError = validationErrors.length > 0 ? validationErrors[0] : undefined

    if (firstValidationError?.category) {
      const categoryName = t(firstValidationError.category)
      firstValidationError = { ...firstValidationError, category: categoryName }
    }

    return getReplaceParams(firstValidationError)
  }, [t, validationErrors])

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    setNewJustificationMessage(
      validationErrors.length > 0 ? (validationErrors[0].justificationMessage ?? '') : ''
    )
  }, [validationErrors])

  /** -------------------- Render ----------------------- */

  return (
    <PopPaper className={styles.root}>
      <FinSobValidationPopupHeader
        inputCount={inputCount}
        inputIndex={inputIndex}
        onNextInput={onNextInput}
        onPrevInput={onPrevInput}
      />

      <section className={styles.content}>
        <div className={styles.validationMessages}>
          {Object.entries(groupedValidationErrors).map(([title, validationErrors]) => {
            const firstValidationError =
              validationErrors.length > 0 ? validationErrors[0] : undefined
            const currentValuesSuffix = firstValidationError?.isSob ? '%' : ''

            return (
              <div key={title}>
                <div className={styles.title}>
                  <Translation text={tr.title} replaceParams={commonReplaceParams} />
                  <Translation span text={tr.subTitle} replaceParams={commonReplaceParams} />
                  <span className={cn(styles.currentValue, styles.space)}>
                    {firstValidationError?.currentValue ?? ''}
                    {currentValuesSuffix}
                  </span>
                </div>
                <ul className={styles.list}>
                  {validationErrors.map((validationError, index) => {
                    const compareToValuesSuffix = validationError?.isSob ? '%' : ''
                    const diffSuffix = validationError?.isSob ? 'pts' : '%'
                    const isLinkActive = inputIndex === index

                    return (
                      <li key={index}>
                        <a
                          className={cn(styles.label, {
                            [styles.labelActive]: isLinkActive,
                          })}
                          onClick={() => onSelectInput(index)}
                        >
                          {validationError?.isShortCompareToKeyOnly ? (
                            <Translation span text={validationError?.compareToKey ?? ''} />
                          ) : (
                            <>
                              <Translation span text={tr.compareToDiff} />
                              &nbsp;
                              <Translation span text={validationError?.compareToKey ?? ''} />
                              <span
                                className={cn(styles.space, {
                                  [styles.warning]: !validationError?.isJustificationRequired,
                                  [styles.error]: validationError?.isJustificationRequired,
                                })}
                              >
                                {validationError?.diff ?? ''}
                                {diffSuffix}
                              </span>
                              <span className={cn(styles.compareToValueName, styles.space)}>
                                {validationError?.comparisonValuePeriod ?? ''}
                              </span>
                              <span className={cn(styles.compareToValue, styles.space)}>
                                {validationError?.comparisonValue ?? ''}
                                {compareToValuesSuffix}
                              </span>
                            </>
                          )}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </div>

        <div className={styles.bottomBlock}>
          <div className={cn({ [styles.bottomMessage]: !isShowJustificationMessage })}>
            <Translation
              span
              text={isShowJustificationMessage ? tr.justificationQuestion : tr.confirmationQuestion}
            />
          </div>

          {isShowJustificationMessage && (
            <Textarea
              placeholder={tr.justificationMessagePlaceholder}
              value={newJustificationMessage}
              onChange={(val: string) => setNewJustificationMessage(val)}
            />
          )}
        </div>
      </section>

      <footer className={styles.footer}>
        <div>
          {isShowNavigation && (
            <div className={styles.changesToConfirm}>
              <Translation
                span
                text={tr.changesToConfirm}
                replaceParams={[
                  { key: '{needsToConfirm}', value: (inputsUnconfirmed ?? 0).toString() },
                  { key: '{totalChanges}', value: (inputCount ?? 0).toString() },
                ]}
              />
            </div>
          )}
        </div>
        <div>
          <Button
            className={styles.button}
            disabled={!isApplyEnabled}
            onClick={() => {
              onApply(newJustificationMessage)
            }}
          >
            <Translation text={applyButtonTranslation} />
          </Button>
          <Button className={styles.button} color="grey" onClick={onCancel}>
            <Translation text={tr.cancel} />
          </Button>
        </div>
      </footer>
    </PopPaper>
  )
}
